import React, { useState, useRef, useEffect, useCallback } from 'react';
import { X, Clock, CheckCircle2, Loader2, Phone, AlertCircle } from 'lucide-react';
import useModalA11y from '../hooks/useModalA11y';
import { trackReservationSubmit } from '../utils/analytics';
import { getStoreContact } from '../constants/stores';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * ReservationModal (Sprint 2A / FELADAT 1)
 *
 * 24 órás készülékfoglalás – NEM webshop, NEM fizetés. A vevő nyomot hagy,
 * mi félretesszük. A lejáratot és a „már foglalt” esetet a BACKEND dönti el
 * (egyetlen igazságforrás); a kliens csak megjeleníti az eredményt.
 *
 * UX (FELADAT 5): loading state, submit alatt tiltott gomb, valódi sikerkép
 * (nem alert), mezőszintű hibaüzenetek, mobilra optimalizált (16px inputok a
 * iOS-zoom ellen, teljes szélességű gombok).
 */
export default function ReservationModal({ device, onClose, onSuccess, expiryHours = 24 }) {
  const [form, setForm] = useState({ name: '', phone: '', email: '', note: '' });
  const [errors, setErrors] = useState({});
  const [state, setState] = useState('idle');   // idle | loading | success | error
  const [serverError, setServerError] = useState('');
  const [result, setResult] = useState(null);
  const modalRef = useRef(null);
  const honeypotRef = useRef(null);
  const firstFieldRef = useRef(null);

  const close = useCallback(() => onClose && onClose(), [onClose]);
  useModalA11y(modalRef, { active: true, onEscape: close });

  useEffect(() => { firstFieldRef.current?.focus(); }, []);

  const contact = getStoreContact(device?.store || device?.store_location);
  // A lejárati idő a BACKEND konfigurációjából jön (RESERVATION_EXPIRY_HOURS);
  // a 24 csak akkor él, ha a státusz-lekérdezés nem futott le.
  const hours = result?.hours || expiryHours;

  // ── Kliens-oldali validáció (a szerveré a döntő – ez csak gyors visszajelzés)
  const validate = () => {
    const e = {};
    const name = form.name.trim();
    const digits = form.phone.replace(/\D/g, '');
    if (name.length < 2) e.name = 'Kérlek add meg a neved.';
    if (name.length > 80) e.name = 'A név túl hosszú (max. 80 karakter).';
    if (digits.length < 6 || digits.length > 15) e.phone = 'Adj meg egy érvényes telefonszámot.';
    if (form.email.trim() && !/^[^@\s]+@[^@\s.]+\.[^@\s]{2,}$/.test(form.email.trim()))
      e.email = 'Ez az e-mail cím nem tűnik érvényesnek.';
    if (form.note.length > 500) e.note = 'A megjegyzés túl hosszú (max. 500 karakter).';
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const submit = async (ev) => {
    ev.preventDefault();
    if (state === 'loading') return;          // dupla-submit védelem
    if (!validate()) return;
    setState('loading');
    setServerError('');
    try {
      const res = await fetch(`${API}/reservations`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          device_id: device?.uid,
          model: device?.name,
          price: device?.rawPrice,
          store: device?.store || device?.store_location || undefined,
          name: form.name.trim(),
          phone: form.phone.trim(),
          email: form.email.trim() || undefined,
          note: form.note.trim() || undefined,
          source_page: window.location.pathname,
          // Honeypot – rejtett mező; ember sosem tölti ki
          website: honeypotRef.current?.value || '',
        }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        setState('error');
        setServerError(data.detail || 'Nem sikerült elküldeni. Kérlek hívj minket telefonon.');
        return;
      }
      setResult(data);
      setState('success');
      onSuccess && onSuccess(data);   // a szülő ettől vált „lefoglalva” állapotra
      trackReservationSubmit({
        // A szerver által adott foglalás-azonosító – ez köti össze a GA4/PostHog
        // eseményt a Lead Center rekordjával (foglalás → átvétel riport).
        reservation_id: data?.id,
        device_id: device?.uid,
        model: device?.name,
        price: device?.rawPrice,
        store: device?.store || device?.store_location,
      });
    } catch {
      setState('error');
      setServerError('Hálózati hiba. Kérlek próbáld újra, vagy hívj minket.');
    }
  };

  const field = (key) => ({
    value: form[key],
    onChange: (e) => {
      setForm((f) => ({ ...f, [key]: e.target.value }));
      if (errors[key]) setErrors((x) => ({ ...x, [key]: undefined }));
    },
    'aria-invalid': errors[key] ? 'true' : undefined,
    'aria-describedby': errors[key] ? `res-err-${key}` : undefined,
    // text-base = 16px → iOS nem zoomol fókuszkor
    className: `w-full border rounded-xl px-4 py-3 text-base outline-none transition-colors ${
      errors[key] ? 'border-red-400 bg-red-50/50' : 'border-gray-200 focus:border-brand'
    }`,
  });

  const Err = ({ k }) => errors[k] ? (
    <p id={`res-err-${k}`} role="alert" className="text-xs text-red-600 mt-1 flex items-center gap-1">
      <AlertCircle size={13} aria-hidden="true" /> {errors[k]}
    </p>
  ) : null;

  return (
    <div
      className="fixed inset-0 z-[9998] bg-black/60 backdrop-blur-sm flex items-end sm:items-center justify-center p-0 sm:p-4 overflow-y-auto"
      onClick={close}
      data-testid="reservation-modal"
    >
      <div
        ref={modalRef}
        role="dialog"
        aria-modal="true"
        aria-label={`${device?.name || 'Készülék'} – félretétel ${hours} órára`}
        onClick={(e) => e.stopPropagation()}
        className="bg-white w-full sm:max-w-md rounded-t-3xl sm:rounded-3xl shadow-2xl max-h-[92vh] overflow-y-auto"
      >
        {/* Fejléc */}
        <div className="flex items-start justify-between gap-3 p-5 border-b border-gray-100 sticky top-0 bg-white rounded-t-3xl">
          <div>
            <h2 className="font-bold text-ink text-lg leading-tight">
              {state === 'success' ? 'Félretettük!' : `Félretesszük ${hours} órára`}
            </h2>
            {device?.name && <p className="text-sm text-gray-500 mt-0.5">{device.name}</p>}
          </div>
          <button
            type="button"
            onClick={close}
            aria-label="Bezárás"
            className="p-2 -m-1 rounded-full hover:bg-gray-100 text-gray-500 flex-shrink-0"
          >
            <X size={20} />
          </button>
        </div>

        {state === 'success' ? (
          /* ── Valódi sikeroldal (nem alert) ── */
          <div className="p-6 text-center" role="status" data-testid="reservation-success">
            <div className="w-16 h-16 rounded-full bg-brand/10 flex items-center justify-center mx-auto mb-4">
              <CheckCircle2 size={34} className="text-brand-hover" />
            </div>
            <p className="font-semibold text-ink mb-2">
              {device?.name ? `A(z) ${device.name} a tiéd ${hours} órán át.` : 'Megkaptuk a foglalásod.'}
            </p>
            <p className="text-sm text-gray-600 mb-4">
              Hamarosan felhívunk a megadott számon, és egyeztetjük az átvételt.
              {result?.expires_at && (
                <>
                  {' '}A foglalás eddig érvényes:{' '}
                  <strong className="text-ink">
                    {new Date(result.expires_at).toLocaleString('hu-HU', { month: 'long', day: 'numeric', hour: '2-digit', minute: '2-digit' })}
                  </strong>.
                </>
              )}
            </p>
            {/* A 4 jegyű egyedi azonosító – ezt bemondva azonnal beazonosítjuk
                a konkrét darabot, két azonos modell között is. */}
            {device?.uid && (
              <div className="bg-white border border-gray-200 rounded-xl p-3 mb-4" data-testid="reservation-device-id">
                <p className="text-xs text-gray-500 mb-0.5">Készülék azonosító (IMEI vége)</p>
                <p className="font-mono font-bold text-ink text-xl tracking-widest">{device.uid}</p>
                <p className="text-xs text-gray-500 mt-1">Ezt mondd be, ha telefonálsz vagy bejössz.</p>
              </div>
            )}
            <div className="bg-gray-50 rounded-xl p-3 text-sm text-gray-600 mb-5">
              Nem tudsz várni? Hívj minket:{' '}
              <a href={contact.telHref} className="text-brand font-semibold hover:underline">
                {contact.phoneDisplay}
              </a>
            </div>
            <button
              type="button"
              onClick={close}
              className="w-full py-3.5 bg-brand text-white font-bold rounded-xl hover:bg-brand-hover transition-colors"
            >
              Rendben
            </button>
          </div>
        ) : (
          <form onSubmit={submit} className="p-5 space-y-4" noValidate>
            <div className="flex items-start gap-2 bg-[#EAF8EC] border border-brand/20 rounded-xl p-3">
              <Clock size={18} className="text-brand flex-shrink-0 mt-0.5" aria-hidden="true" />
              <p className="text-sm text-gray-700">
                {hours} órára félretesszük neked. <strong className="text-ink">Nem fizetsz most semmit</strong> —
                csak megbeszéljük, mikor tudsz érte jönni.
              </p>
            </div>

            <div>
              <label htmlFor="res-name" className="block text-sm font-semibold text-ink mb-1.5">
                Neved <span className="text-red-600" aria-hidden="true">*</span>
              </label>
              <input id="res-name" ref={firstFieldRef} type="text" autoComplete="name"
                maxLength={80} placeholder="Pl. Kovács Béla" required {...field('name')} />
              <Err k="name" />
            </div>

            <div>
              <label htmlFor="res-phone" className="block text-sm font-semibold text-ink mb-1.5">
                Telefonszámod <span className="text-red-600" aria-hidden="true">*</span>
              </label>
              <input id="res-phone" type="tel" inputMode="tel" autoComplete="tel"
                maxLength={32} placeholder="+36 30 123 4567" required {...field('phone')} />
              <Err k="phone" />
            </div>

            <div>
              <label htmlFor="res-email" className="block text-sm font-semibold text-ink mb-1.5">
                E-mail <span className="text-gray-500 font-normal">(nem kötelező)</span>
              </label>
              <input id="res-email" type="email" inputMode="email" autoComplete="email"
                maxLength={200} placeholder="pelda@email.hu" {...field('email')} />
              <Err k="email" />
            </div>

            <div>
              <label htmlFor="res-note" className="block text-sm font-semibold text-ink mb-1.5">
                Megjegyzés <span className="text-gray-500 font-normal">(nem kötelező)</span>
              </label>
              <textarea id="res-note" rows={2} maxLength={500}
                placeholder="Pl. holnap délután tudok érte menni" {...field('note')} />
              <Err k="note" />
            </div>

            {/* Honeypot – rejtve az embertől, a botok kitöltik */}
            <input
              ref={honeypotRef}
              type="text"
              name="website"
              tabIndex={-1}
              autoComplete="off"
              aria-hidden="true"
              style={{ position: 'absolute', left: '-9999px', width: 1, height: 1, opacity: 0 }}
            />

            {serverError && (
              <div role="alert" className="flex items-start gap-2 bg-red-50 border border-red-200 rounded-xl p-3">
                <AlertCircle size={16} className="text-red-600 flex-shrink-0 mt-0.5" aria-hidden="true" />
                <div className="text-sm text-red-700">
                  {serverError}
                  <a href={contact.telHref} className="block mt-1 font-semibold underline">
                    Hívás: {contact.phoneDisplay}
                  </a>
                </div>
              </div>
            )}

            <button
              type="submit"
              disabled={state === 'loading'}
              data-testid="reservation-submit"
              className="w-full flex items-center justify-center gap-2 py-4 bg-brand text-white font-bold rounded-xl hover:bg-brand-hover transition-colors disabled:opacity-60 disabled:cursor-not-allowed"
            >
              {/* A beküldő gomb első személyben, ugyanúgy, mint az adatlap
                  gombja – a kattintás után ne váltson hangot a folyamat.
                  A modál CÍME marad bolti hangú („Félretesszük…”): az a mi
                  ígéretünk, ez itt a látogató cselekvése. */}
              {state === 'loading'
                ? (<><Loader2 size={18} className="animate-spin" aria-hidden="true" /> Küldés…</>)
                : (<><Clock size={18} aria-hidden="true" /> Lefoglalom {hours} órára</>)}
            </button>

            <p className="text-[11px] leading-relaxed text-gray-500 text-center">
              A foglalással hozzájárulsz, hogy a megadott telefonszámon felhívjunk.
              Nem küldünk reklámot. <a href={contact.telHref} className="text-brand underline inline-flex items-center gap-1">
                <Phone size={11} aria-hidden="true" /> Inkább telefonálnék
              </a>
            </p>
          </form>
        )}
      </div>
    </div>
  );
}
